"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SalaryRangeFilter = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = __importDefault(require("react"));
const react_intl_1 = require("react-intl");
const useIsFirstRender_1 = require("@src/hooks/useIsFirstRender");
const config_1 = require("@src/config");
const types_1 = require("@src/types");
const Filter_style_1 = require("./filter/Filter.style");
const offer_filter_context_1 = require("../../offer-filter-context");
const SalaryRangeFilter_style_1 = require("./SalaryRangeFilter.style");
const min = 0;
var ChangeType;
(function (ChangeType) {
    ChangeType["MIN"] = "min";
    ChangeType["MAX"] = "max";
})(ChangeType || (ChangeType = {}));
const steps = {
    default: 10,
    [types_1.SalaryRate.hourly]: 0.1,
};
const SalaryRangeFilter = () => {
    const config = (0, config_1.getConfig)();
    const intl = (0, react_intl_1.useIntl)();
    const isFirstRender = (0, useIsFirstRender_1.useIsFirstRender)();
    const { salaryFilter, setSalaryFilter, getHighestSalary } = (0, offer_filter_context_1.useOffersFilterContextValue)();
    const [maxBoundary, setMaxBoundary] = react_1.default.useState(salaryFilter.maxRange);
    const [localMaxRange, setLocalMaxRange] = react_1.default.useState(salaryFilter.maxRange);
    const [localMinRange, setLocalMinRange] = react_1.default.useState(salaryFilter.minRange);
    const sliderRef = react_1.default.useRef(null);
    const [localSalaryRate, setLocalSalaryRate] = react_1.default.useState(salaryFilter.rate);
    const [isContextChange, setIsContextChange] = react_1.default.useState(false);
    const radioOptions = config.salaryRates.map(rate => ({
        label: intl.formatMessage({ id: `offer.filter.${rate.value}` }),
        value: rate.value,
    }));
    const setLocalAndContextStates = (highestSalary) => {
        setMaxBoundary(highestSalary);
        if (salaryFilter.rate) {
            setLocalMinRange(0);
        }
        if (!isFirstRender) {
            setSalaryFilter({
                highestSalary,
                rate: localSalaryRate,
                minRange: salaryFilter.rate ? 0 : localMinRange,
                maxRange: localMaxRange !== maxBoundary && !salaryFilter.rate ? localMaxRange : highestSalary,
            });
        }
        if (localMaxRange !== maxBoundary && !salaryFilter.rate) {
            return;
        }
        setLocalMaxRange(highestSalary);
    };
    const handleSalaryRateChange = async () => {
        const highestSalary = await getHighestSalary(localSalaryRate);
        if (highestSalary) {
            setLocalAndContextStates(highestSalary);
        }
    };
    react_1.default.useEffect(() => {
        if (!isContextChange && !isFirstRender) {
            handleSalaryRateChange();
        }
        else {
            setIsContextChange(false);
        }
    }, [localSalaryRate]);
    react_1.default.useEffect(() => {
        // here we handle outside changes to context state and load them to local state
        const didContextRateChange = salaryFilter.rate !== localSalaryRate;
        // this handle use-case for init load states, when salaryFilter is off & we need to set max value on local
        const didContextHighestSalaryChange = salaryFilter.highestSalary !== maxBoundary;
        if (didContextRateChange || didContextHighestSalaryChange) {
            if (didContextRateChange) {
                setIsContextChange(true);
            }
            setLocalSalaryRate(salaryFilter.rate);
            setLocalMinRange(salaryFilter.minRange);
            setLocalMaxRange(salaryFilter.maxRange);
            setMaxBoundary(salaryFilter.highestSalary);
        }
        else {
            setIsContextChange(false);
        }
    }, [salaryFilter.rate, salaryFilter.highestSalary]);
    react_1.default.useEffect(() => {
        setLocalMinRange(salaryFilter.minRange || 0);
    }, [salaryFilter.minRange]);
    const onSliderChange = (value) => {
        setLocalMinRange(value[0]);
        setLocalMaxRange(value[1]);
    };
    function checkNewMinMaxOrderCorrectness(inputValue, changingInputType) {
        if (changingInputType === ChangeType.MIN) {
            const isNewValueLowerThenMax = inputValue < localMaxRange;
            if (isNewValueLowerThenMax) {
                return true;
            }
            setLocalMinRange(localMaxRange);
            setSalaryFilter({
                ...salaryFilter,
                minRange: localMaxRange,
            });
        }
        if (changingInputType === ChangeType.MAX) {
            const isNewValueHigherThenMin = inputValue > localMinRange;
            if (isNewValueHigherThenMin) {
                return true;
            }
            setLocalMaxRange(localMinRange);
            setSalaryFilter({
                ...salaryFilter,
                maxRange: localMinRange,
            });
        }
        return false;
    }
    const changeInputValue = (value, type) => {
        const isValueNaN = Number.isNaN(value);
        const localInputValue = type === ChangeType.MAX ? localMaxRange : localMinRange;
        const hasValueChanged = value !== localInputValue;
        if (!hasValueChanged || isValueNaN) {
            return;
        }
        if (!localSalaryRate) {
            setLocalSalaryRate(types_1.SalaryRate.monthly);
        }
        const numberValue = Number(value);
        if (numberValue <= 0 && type === ChangeType.MAX) {
            setLocalMaxRange(maxBoundary);
            setSalaryFilter({
                ...salaryFilter,
                maxRange: maxBoundary,
            });
            return;
        }
        const areMinMaxValuesInRightOrder = checkNewMinMaxOrderCorrectness(numberValue, type);
        if (type === ChangeType.MAX && areMinMaxValuesInRightOrder) {
            const newMaxValue = numberValue <= maxBoundary && numberValue !== 0 ? numberValue : maxBoundary;
            const roundedNewMaxValue = salaryFilter.rate === types_1.SalaryRate.hourly
                ? Number(newMaxValue.toFixed(2))
                : Number(newMaxValue.toFixed(0));
            setLocalMaxRange(roundedNewMaxValue);
            setSalaryFilter({
                ...salaryFilter,
                maxRange: roundedNewMaxValue,
            });
        }
        if (type === ChangeType.MIN && areMinMaxValuesInRightOrder) {
            const roundedNeMinValue = salaryFilter.rate === types_1.SalaryRate.hourly
                ? Number(numberValue.toFixed(2))
                : Number(numberValue.toFixed(0));
            setLocalMinRange(roundedNeMinValue);
            setSalaryFilter({
                ...salaryFilter,
                minRange: roundedNeMinValue,
            });
        }
    };
    const onSliderRelease = value => {
        if (!localSalaryRate) {
            setLocalSalaryRate(types_1.SalaryRate.monthly);
        }
        else {
            setSalaryFilter({
                ...salaryFilter,
                minRange: value[0],
                maxRange: value[1],
            });
        }
        ;
        sliderRef.current.blur();
    };
    return ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)(Filter_style_1.FilterTitle, { children: (0, jsx_runtime_1.jsx)(react_intl_1.FormattedMessage, { id: "offers.filter.salary_rate_title" }) }), (0, jsx_runtime_1.jsx)(Filter_style_1.FilterSeparator, {}), (0, jsx_runtime_1.jsxs)("div", { className: "m-b-l", children: [(0, jsx_runtime_1.jsx)("div", { className: "flex-center", children: (0, jsx_runtime_1.jsx)(SalaryRangeFilter_style_1.SalaryRateButtons, { options: radioOptions, activeButton: localSalaryRate, setActiveValue: value => setLocalSalaryRate(value), size: "middle" }) }), (0, jsx_runtime_1.jsx)(SalaryRangeFilter_style_1.StyledSlider, { range: true, min: min, max: maxBoundary, value: [localMinRange, localMaxRange], onChange: onSliderChange, step: salaryFilter.rate === types_1.SalaryRate.hourly ? steps[salaryFilter.rate] : steps.default, onAfterChange: onSliderRelease, ref: sliderRef, tooltipVisible: false }), (0, jsx_runtime_1.jsxs)("div", { className: "flex-row align-center justify-space-between", children: [(0, jsx_runtime_1.jsxs)("div", { className: "flex-col", children: [(0, jsx_runtime_1.jsx)("div", { children: intl.formatMessage({ id: 'minimum' }) }), (0, jsx_runtime_1.jsx)(SalaryRangeFilter_style_1.InputMinMax, { className: "m-r-s", decimalSeparator: ",", placeholder: `${min}`, value: localMinRange === min ? NaN : localMinRange, onBlur: e => changeInputValue(Number(e.target.value) <= min ? min : Number(e.target.value), ChangeType.MIN), onPressEnter: e => e.target.blur(), min: min, max: maxBoundary, addonAfter: "\u20AC", precision: salaryFilter.rate === types_1.SalaryRate.hourly ? 2 : 0, controls: false })] }), (0, jsx_runtime_1.jsx)("span", { className: "m-t-m", children: "-" }), (0, jsx_runtime_1.jsxs)("div", { className: "flex-col", children: [(0, jsx_runtime_1.jsx)("div", { children: intl.formatMessage({ id: 'maximum' }) }), (0, jsx_runtime_1.jsx)(SalaryRangeFilter_style_1.InputMinMax, { decimalSeparator: ",", placeholder: intl.formatMessage({
                                            id: 'offer.filter.unlimited',
                                        }), value: localMaxRange >= maxBoundary ? NaN : localMaxRange, onBlur: e => changeInputValue(Number(e.target.value) >= maxBoundary ? maxBoundary : Number(e.target.value), ChangeType.MAX), onPressEnter: e => e.target.blur(), min: min, precision: salaryFilter.rate === types_1.SalaryRate.hourly ? 2 : 0, max: maxBoundary, addonAfter: "\u20AC", controls: false })] })] })] })] }));
};
exports.SalaryRangeFilter = SalaryRangeFilter;
